import { useMutation, useQuery, useQueryClient } from 'react-query'
import { mapToNetworkList } from 'api/mappers/networkList'
import {
  AddMerchantToMerchantListRequest,
  CreateMerchantListRequest,
  DeleteMerchantFromMerchantListRequest,
  DeleteMerchantListRequest,
  GetMerchantListRequest,
  GetMerchantListsRequest,
  MerchantList,
  MerchantListType,
  UpdateMerchantListRequest,
} from 'lib/generated/api'
import {
  mapToMerchantList,
  mapToMerchantLists,
} from 'api/mappers/merchantLists'
import { mapToRestaurantList } from 'api/mappers/restaurantList'
import { useApiClient } from 'contexts/ApiClientContext'

export const QUERY_KEYS = {
  merchantList: 'merchantList',
  merchantLists: 'merchantLists',
  createMerchantList: 'createMerchantList',
} as const

/* Merchant List */

export const merchantListQueryKey = (request: GetMerchantListRequest) => [
  QUERY_KEYS.merchantList,
  request.organizationId,
  request.workspaceId,
  request.merchantListId,
]

export const useMerchantListQuery = (request: GetMerchantListRequest) => {
  const { merchantListApi } = useApiClient()
  return useQuery(
    merchantListQueryKey(request),
    () => merchantListApi.getMerchantList(request),
    {
      enabled:
        !!request.organizationId &&
        !!request.workspaceId &&
        !!request.merchantListId,
      select: mapToMerchantList,
    }
  )
}

export const useUpdateMerchantList = () => {
  const { merchantListApi } = useApiClient()
  const queryClient = useQueryClient()
  return useMutation(
    (request: UpdateMerchantListRequest) =>
      merchantListApi.updateMerchantList(request),
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData(merchantListQueryKey(variables), data)
      },
    }
  )
}

export const useAddMerchantToMerchantList = () => {
  const { merchantListApi } = useApiClient()
  const queryClient = useQueryClient()
  return useMutation(
    (request: AddMerchantToMerchantListRequest) =>
      merchantListApi.addMerchantToMerchantList(request),
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData(merchantListQueryKey(variables), data)
      },
    }
  )
}

export const useDeleteMerchantFromMerchantList = () => {
  const { merchantListApi } = useApiClient()
  const queryClient = useQueryClient()
  return useMutation(
    (request: DeleteMerchantFromMerchantListRequest) =>
      merchantListApi.deleteMerchantFromMerchantList(request),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(
          merchantListQueryKey({
            organizationId: variables.organizationId,
            workspaceId: variables.workspaceId,
            merchantListId: variables.merchantListId,
          })
        )
      },
    }
  )
}

/* Merchant Lists */

export const merchantListsQueryKey = ({
  organizationId,
  workspaceId,
  ...queryParams
}: GetMerchantListsRequest) => [
  QUERY_KEYS.merchantLists,
  organizationId,
  workspaceId,
  queryParams,
]

export const useGetMerchantLists = (request: GetMerchantListsRequest) => {
  const { merchantListApi } = useApiClient()
  return useQuery(
    merchantListsQueryKey(request),
    () => merchantListApi.getMerchantLists(request),
    {
      enabled: !!request.organizationId && !!request.workspaceId,
    }
  )
}

export const useNetworkListQuery = (
  organizationId: string,
  workspaceId: string
) => {
  const { merchantListApi } = useApiClient()
  const request: GetMerchantListsRequest = {
    organizationId,
    workspaceId,
    type: [MerchantListType.SystemPropertyManagerMerchantNetwork],
  }
  return useQuery(
    merchantListsQueryKey(request),
    () => merchantListApi.getMerchantLists(request),
    {
      enabled: !!organizationId && !!workspaceId,
      select: mapToNetworkList,
    }
  )
}

export const useCreateMerchantList = (
  onSuccess: (merchantList: MerchantList) => void
) => {
  const { merchantListApi } = useApiClient()
  const queryClient = useQueryClient()
  return useMutation(
    (request: CreateMerchantListRequest) =>
      merchantListApi.createMerchantList(request),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(
          merchantListsQueryKey({
            organizationId: variables.organizationId,
            workspaceId: variables.workspaceId,
          })
        )
        onSuccess(data)
      },
    }
  )
}

export const useDeleteMerchantList = () => {
  const { merchantListApi } = useApiClient()
  const queryClient = useQueryClient()
  return useMutation(
    (request: DeleteMerchantListRequest) =>
      merchantListApi.deleteMerchantList(request),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(
          merchantListsQueryKey({
            organizationId: variables.organizationId,
            workspaceId: variables.workspaceId,
          })
        )
      },
    }
  )
}

export const useRestaurantListsQuery = (request: GetMerchantListsRequest) => {
  const { merchantListApi } = useApiClient()
  return useQuery(
    merchantListsQueryKey(request),
    () =>
      merchantListApi.getMerchantLists({
        ...request,
      }),
    { select: mapToRestaurantList }
  )
}

export const useGetUserDefinedMerchantLists = (
  request: GetMerchantListsRequest,
  enabled: boolean
) => {
  const { merchantListApi } = useApiClient()
  return useQuery(
    merchantListsQueryKey(request),
    () =>
      merchantListApi.getMerchantLists({
        ...request,
        excludeType: [
          MerchantListType.SystemAllMerchants,
          MerchantListType.SystemPropertyManagerMerchantNetwork,
        ],
      }),
    {
      enabled: !!request.organizationId && !!request.workspaceId && enabled,
      select: mapToMerchantLists,
    }
  )
}
